/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { Redirect } from 'react-router';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import ContentFrame from '../../../../applicationFrame/ContentFrame';
import { ScrollAwareContext, ScrollAwareScroller, ScrollAwareAppNavStep } from '../../../../components/ScrollAware';
import { AppHeaderScrollableText } from '../../../../components/appNav/AppHeaderScrollable';

import { actions as ownerActions, selectors as ownerSelector } from '../../../../redux/ducks/owners';
import { actions as systemModalActions } from '../../../../redux/ducks/systemModal';
import { getActiveProfile, getMainProfileId } from '../../../redux/reduxSelectors';
import { navigate, navigateBack } from '../../../redux/NavigationActions';
import ProfileActions from '../../../redux/ProfileActions';

import { ProfileThemeedWarningModal } from '../../../../components/modal/WarningModal';
import RouteNames from '../../../RouteNames';
import ContentWrapper from '../../../../components/ContentWrapper';
import Card, { ExternalCardTitle, CardSection, CardDestructiveButton } from '../../../../components/card/Card';
import { ProfileThemeMarkdown } from '../../../../components/Markdown';

function mapStateToProps(state, routerProps) {
  const ownerProfileId = Number(routerProps.match.params.ownerProfileId);
  const sharedProfile = getActiveProfile(state);
  const mainProfileId = getMainProfileId(state);
  const otherOwners = ownerSelector.getActiveOwnersForProfile(sharedProfile.data.id, state);
  const ownerProfile = ownerSelector.getActiveOwnerForProfile(sharedProfile.data.id, ownerProfileId, state);

  return {
    profileName: sharedProfile.greetingName(),
    sharedProfile,
    ownerProfile,
    ownerProfileId,
    isSharedProfileMainUser: sharedProfile.data.id === mainProfileId,
    isOwnerMainProfile: ownerProfileId === mainProfileId,
    hasOtherOwners: otherOwners.length > 1,
    goBack: () => {
      navigate(RouteNames.profileAccess());
    },
  };
}

function mapDispatchToProps(dispatch) {
  return {
    showConfirmRemoveOwnership: (modalContent) => {
      dispatch(systemModalActions.show(modalContent));
    },
    removeOwnershipForOther: (profileId, ownerProfile) => {
      dispatch(ownerActions.removeOwner(profileId, ownerProfile.data.id, () => {
        ProfileActions.requestAllProfiles();
        navigateBack(RouteNames.profileAccess());
      }));
    },
    removeMyOwnership: (profileId, ownerProfile) => {
      dispatch(ownerActions.removeOwner(profileId, ownerProfile.data.id, () => {
        ProfileActions.requestAllProfiles();
        dispatch(ProfileActions.switchToProfile(ownerProfile.data))
          .then(() => {
            navigate(RouteNames.myBackpackAll());
          });
      }));
    },
  };
}

interface Props {
  sharedProfile: any;
  ownerProfile: any;
  ownerProfileId: number;
  goBack(): void;
  profileName: string;
  showConfirmRemoveOwnership(content): void;
  removeOwnershipForOther(profileId, ownerProfile): void;
  removeMyOwnership(profileId, ownerProfile): void;
  isSharedProfileMainUser: boolean;
  isOwnerMainProfile: boolean;
  hasOtherOwners: boolean;
}

const MESSAGES = {
  otherOwnerAccessToNotUser: {
    title: 'accessPermissions.notUserAccess.title',
    label: 'accessPermissions.userAccessToNotUser.field.label',
    button: 'accessPermissions.removeAccess.notUser.button.label',
  },
  userAccessToNotUser: {
    title: 'accessPermissions.userAccess.title',
    label: 'accessPermissions.userAccessToNotUser.field.label',
    button: 'accessPermissions.removeAccess.User.button.label',
  },
  otherOwnerAccessToUser: {
    title: 'accessPermissions.notUserAccess.title',
    label: 'accessPermissions.notUserAccessToUser.field.label',
    button: 'accessPermissions.removeAccess.notUser.button.label',
  },
  userAccessToUser: {
    title: 'accessPermissions.userAccess.title',
    label: 'accessPermissions.userAccessToUser.field.label',
    button: '',
  },
};

class ViewAccessPermissions extends React.Component<Props, {}> {

  onRemoveOwnership = () => {
    const { isSharedProfileMainUser } = this.props;

    const messages = !isSharedProfileMainUser ? {
      title: { id: 'removeUserAccess.forNotUser.title.text' },
      subheading: { id: 'removeUserAccess.forNotUser.heading' },
      body: { id: 'removeUserAccess.forNotUser.text' },
      button: { id: 'removeUserAccess.forNotUser.confirm.button.label' },
    } : {
      title: { id: 'removeNotUserAccess.forUser.title.text' },
      subheading: { id: 'removeNotUserAccess.forUser.heading' },
      body: { id: 'removeNotUserAccess.forUser.text' },
      button: { id: 'removeNotUserAccess.forUser.confirm.button.label' },
    };

    this.props.showConfirmRemoveOwnership(
      <ProfileThemeedWarningModal
        hasCloseButton={true}
        phraseMessages={messages}
        onConfirm={() => {
          if (isSharedProfileMainUser) {
            this.props.removeOwnershipForOther(this.props.sharedProfile.data.id, this.props.ownerProfile);
          } else {
            this.props.removeMyOwnership(this.props.sharedProfile.data.id, this.props.ownerProfile);
          }
        }}
      />
    );
  }

  onDeleteBackpack = () => {
    navigate(RouteNames.profileDelete());
  }

  getMessages = () => {
    const { isSharedProfileMainUser, isOwnerMainProfile } = this.props;

    if (isOwnerMainProfile) {
      if (isSharedProfileMainUser) {
        return MESSAGES.userAccessToUser;
      } else {
        return MESSAGES.userAccessToNotUser;
      }
    } else {
      if (isSharedProfileMainUser) {
        return MESSAGES.otherOwnerAccessToUser;
      } else {
        return MESSAGES.otherOwnerAccessToNotUser;
      }
    }
  }

  handleOnRemoveOwnership = () => {
    const { isSharedProfileMainUser, isOwnerMainProfile } = this.props;

    if (isOwnerMainProfile) {
      if (isSharedProfileMainUser) {
        return MESSAGES.userAccessToNotUser;
      } else {
        return MESSAGES.userAccessToNotUser;
      }
    } else {
      if (isSharedProfileMainUser) {
        return MESSAGES.otherOwnerAccessToUser;
      } else {
        return MESSAGES.otherOwnerAccessToNotUser;
      }
    }
  }

  renderRemoveButton = () => {
    const { isSharedProfileMainUser, ownerProfileId, sharedProfile, hasOtherOwners, isOwnerMainProfile } = this.props;
    const msg = this.getMessages();
    const isMainUserAndMainProfile = isSharedProfileMainUser && ownerProfileId === sharedProfile.data.id;
    if (isMainUserAndMainProfile) {
      return null;
    }

    if ((isOwnerMainProfile && hasOtherOwners) || isSharedProfileMainUser) {
      return (
        <CardDestructiveButton className="" onClick={this.onRemoveOwnership}>
          <FormattedMessage id={msg.button} />
        </CardDestructiveButton>
      );
    }

    return null;
  }

  render() {
    const { ownerProfileId, ownerProfile, goBack, sharedProfile, isSharedProfileMainUser } = this.props;

    const msg = this.getMessages();

    const ownerName = ownerProfile.greetingName();
    const shareProfileName = isSharedProfileMainUser ? ownerProfile.greetingName() : sharedProfile.greetingName();

    if (!ownerProfileId) {
      return <Redirect to={RouteNames.profileAccess()} />;
    }

    return (
      <div>
        <ScrollAwareContext>
          <ScrollAwareAppNavStep
            goBack={goBack}
            color="white"
          />

          <ContentFrame>
            <ScrollAwareScroller>
              <AppHeaderScrollableText
                id={msg.title}
                values={{ string1: ownerName }}
              />
              <ContentWrapper>
                <ExternalCardTitle>
                  <FormattedMessage id={msg.label} values={{ string1: shareProfileName }} />
                </ExternalCardTitle>
                <Card>
                  <CardSection hasBorder={false}>
                    <div>

                      <h3 className="font-body font-color-gsDarkest" style={{ marginBottom: 24 }}>
                        <FormattedMessage id="accessPermissions.fullAccess.title.text" />
                      </h3>
                      <ProfileThemeMarkdown phraseId="accessPermissions.fullAccess.intro.md" />
                    </div>
                  </CardSection>
                </Card>

                {this.renderRemoveButton()}

              </ContentWrapper>
            </ScrollAwareScroller>
          </ContentFrame>
        </ScrollAwareContext>
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ViewAccessPermissions);
